/** Terminal depositional flats and connected tidal creeks at real ocean outlets.
 * Built AFTER river routing; drainage never samples this terminal treatment. */
import type { PlanetDef } from './universe.js';
import type { RiverPolyline } from './rivers.js';
import { hashSeed } from './rng.js';
import { planarField } from './landforms.js';

type V3=readonly [number,number,number];
export interface EstuaryDef {
  readonly id:string;
  readonly riverId:number;
  readonly center:V3;
  readonly inland:V3;
  readonly across:V3;
  readonly inlandM:number;
  readonly offshoreM:number;
  readonly widthM:number;
  readonly channelM:number;
  readonly seed:number;
}
export interface EstuarySample {weight:number;heightM:number;shelter:number;channel:number}
export const estuarySample=():EstuarySample=>({weight:0,heightM:0,shelter:0,channel:0});
const smooth=(x:number):number=>{const t=Math.max(0,Math.min(1,x));return t*t*(3-2*t);};
const norm=(x:number,y:number,z:number):V3=>{const n=Math.sqrt(x*x+y*y+z*z);return [x/n,y/n,z/n];};
export function estuaryDirection(f:EstuaryDef,R:number,u:number,v:number):V3{
  return norm(f.center[0]*R+f.across[0]*u+f.inland[0]*v,f.center[1]*R+f.across[1]*u+f.inland[1]*v,f.center[2]*R+f.across[2]*u+f.inland[2]*v);
}

export class EstuaryField {
  readonly features:readonly EstuaryDef[];
  private readonly cells=new Map<number,readonly EstuaryDef[]>();
  private readonly cellM:number;
  private readonly gridN:number;
  private readonly seaM:number;
  constructor(private readonly def:PlanetDef,rivers:readonly RiverPolyline[],native:(x:number,y:number,z:number)=>number,
    continental:(x:number,y:number,z:number)=>number){
    const features:EstuaryDef[]=[];this.features=features;
    const R=def.radius;this.seaM=(def.seaRadius??R)-R;this.cellM=Math.min(650,R*.1);this.gridN=Math.ceil(R*2/this.cellM)+3;
    if(def.seaRadius===null||def.archetype==='ice'||def.archetype==='volcanic'||def.terrain.amplitudeRel<.001){Object.freeze(features);return;}
    const A=R*def.terrain.amplitudeRel;
    for(const river of rivers){
      const p=river.points.at(-1);if(!p||p.ws*A>this.seaM+.01)continue; // no tributary junctions
      const prev=river.points[Math.max(0,river.points.length-5)],center:V3=[p.x,p.y,p.z];
      let x=prev.x-p.x,y=prev.y-p.y,z=prev.z-p.z;const radial=x*p.x+y*p.y+z*p.z;
      x-=radial*p.x;y-=radial*p.y;z-=radial*p.z;if(x*x+y*y+z*z<1e-15)continue;
      const inland=norm(x,y,z),across:V3=[inland[1]*p.z-inland[2]*p.y,inland[2]*p.x-inland[0]*p.z,inland[0]*p.y-inland[1]*p.x];
      let tidal=0;
      for(let i=river.points.length-2;i>=0;i--){const q=river.points[i];if(q.ws*A>this.seaM+.03)break;tidal=p.s-q.s;}
      const f:EstuaryDef={id:`${def.id}:estuary:${river.id}`,riverId:river.id,center,inland,across,
        inlandM:Math.max(12,Math.min(70,tidal*.8)),offshoreM:Math.min(270,R*.018),widthM:Math.min(165,R*.012),channelM:p.half,
        seed:hashSeed(`${def.seed}:estuary:${river.id}:v1`)};
      // Sea-level lakes also terminate river traces. Require a continuously wet
      // seaward route and submerged continental ground beyond the whole fan.
      let ocean=true;
      for(let v=0;v>=-f.offshoreM*2;v-=20)if(native(...estuaryDirection(f,R,0,v))>this.seaM+.1){ocean=false;break;}
      if(!ocean||continental(...estuaryDirection(f,R,0,-f.offshoreM*2))>this.seaM-12)continue;
      if(features.some(e=>{const dx=e.center[0]-p.x,dy=e.center[1]-p.y,dz=e.center[2]-p.z;return (dx*dx+dy*dy+dz*dz)*R*R<(f.widthM+e.widthM)**2*4;}))continue;
      features.push(Object.freeze({...f,center:Object.freeze(center),inland:Object.freeze(inland),across:Object.freeze(across)}));
    }
    Object.freeze(features);
    const cells=new Map<number,EstuaryDef[]>();
    for(const f of features){
      const bound=Math.sqrt(f.widthM*f.widthM*4+f.offshoreM*f.offshoreM)*1.1;
      const lo=f.center.map(v=>Math.max(0,Math.floor((v*R+R-bound)/this.cellM))),hi=f.center.map(v=>Math.min(this.gridN-1,Math.floor((v*R+R+bound)/this.cellM)));
      for(let x=lo[0];x<=hi[0];x++)for(let y=lo[1];y<=hi[1];y++)for(let z=lo[2];z<=hi[2];z++){
        const key=this.key(x,y,z);let list=cells.get(key);if(!list)cells.set(key,list=[]);list.push(f);
      }
    }
    for(const [key,list] of cells)this.cells.set(key,Object.freeze(list));
  }
  private key(x:number,y:number,z:number):number{return (x*this.gridN+y)*this.gridN+z;}
  sample(dx:number,dy:number,dz:number,out:EstuarySample):EstuarySample{
    out.weight=out.heightM=out.shelter=out.channel=0;
    const R=this.def.radius,c=this.cellM,list=this.cells.get(this.key(Math.floor((dx*R+R)/c),Math.floor((dy*R+R)/c),Math.floor((dz*R+R)/c)));
    if(!list)return out;
    let sum=0,uncovered=1;
    for(const f of list){
      const dot=dx*f.center[0]+dy*f.center[1]+dz*f.center[2];if(dot<=0)continue;
      const u=(dx*f.across[0]+dy*f.across[1]+dz*f.across[2])*R/dot,v=(dx*f.inland[0]+dy*f.inland[1]+dz*f.inland[2])*R/dot;
      if(v>=f.inlandM||v<=-f.offshoreM||Math.abs(u)>f.widthM*1.6)continue;
      const t=Math.max(0,-v/f.offshoreM),scale=Math.min(1,f.widthM/130);
      const bend=(planarField(0,v/130,3.2,f.seed)-planarField(0,0,3.2,f.seed))*35*scale*smooth(t/.22);
      const cross=u-bend,width=f.widthM*(.70+.45*smooth(t/.55));
      const w=smooth((1-Math.abs(cross)/width)/.30)*smooth((f.inlandM-v)/f.inlandM)*smooth((v+f.offshoreM)/(f.offshoreM*.32));
      if(w<=0)continue;
      const primary=f.channelM*(1+1.3*smooth(t/.65));
      const main=1-smooth((Math.abs(cross)-primary*.6)/(primary*.8));
      // Two unequal distributary creeks join the main channel at the mouth and
      // reach open water. Raised shoals are outside every connected channel.
      const fork=width*.64*smooth(t/.38);
      const a=1-smooth((Math.abs(cross-fork)-3*scale)/(9*scale));
      const b=1-smooth((Math.abs(cross+fork*.78)-2*scale)/(7*scale));
      const channel=Math.max(main,Math.max(a,b)*smooth(t/.18));
      const shoal=.45+1.6*planarField(cross/(38*scale),v/(56*scale),6.7,f.seed);
      const target=this.seaM+shoal+(-3.6-shoal)*channel;
      sum+=w;uncovered*=1-w;out.heightM+=target*w;out.shelter+=w*(1-smooth((t-.48)/.5));out.channel+=channel*w;
    }
    if(sum){out.weight=1-uncovered;out.heightM/=sum;out.shelter=out.shelter/sum*out.weight;out.channel/=sum;}
    return out;
  }
}
